'use client'

import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/shared/components/ui/popover'

const CHURCH_ORG_SUGGESTIONS_MAX = 50

type ChurchOrgComboboxProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

export function ChurchOrgCombobox({
  value,
  onChange,
  placeholder = 'Church or org name',
  disabled = false,
}: ChurchOrgComboboxProps) {
  const [isListOpen, setIsListOpen] = useState(false)
  const [localQuery, setLocalQuery] = useState('')

  const { data } = useQuery({
    queryKey: ['volunteers-organization-names'],
    queryFn: async () => {
      const res = await fetch('/api/volunteers/organization-names')
      if (!res.ok) throw new Error('Failed to fetch organization names')
      const json = await res.json()
      const arr = json.success && json.data ? json.data : []
      return Array.isArray(arr) ? arr.filter((n): n is string => typeof n === 'string' && n.trim() !== '') : []
    },
  })
  const organizationNames = useMemo(() => (data ?? []) as string[], [data])

  const suggestions = useMemo(() => {
    const q = localQuery.trim().toLowerCase()
    if (!q) return organizationNames.slice(0, CHURCH_ORG_SUGGESTIONS_MAX)
    return organizationNames
      .filter((name) => name.toLowerCase().includes(q))
      .slice(0, CHURCH_ORG_SUGGESTIONS_MAX)
  }, [organizationNames, localQuery])

  const showList = isListOpen && suggestions.length > 0

  useEffect(() => {
    if (value !== localQuery) {
      setLocalQuery('')
    }
  }, [value, localQuery])

  const handleSelect = (name: string) => {
    onChange(name)
    setLocalQuery('')
    setIsListOpen(false)
  }

  return (
    <Popover open={showList && !disabled} onOpenChange={(open) => !disabled && setIsListOpen(open)}>
      <PopoverAnchor asChild>
        <Input
          placeholder={placeholder}
          value={localQuery !== '' ? localQuery : value}
          onChange={(e) => {
            if (disabled) return
            const v = (e.target as HTMLInputElement).value
            setLocalQuery(v)
            onChange(v)
            setIsListOpen(true)
          }}
          onFocus={() => !disabled && setIsListOpen(true)}
          disabled={disabled}
          className={disabled ? 'bg-muted cursor-not-allowed' : undefined}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-h-48 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-48 overflow-y-auto">
          {suggestions.map((name) => (
            <button
              key={name}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
              onPointerDown={(e) => {
                e.preventDefault()
                handleSelect(name)
              }}
            >
              {name}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
